import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useReducer,
} from 'react';
import PropTypes from 'prop-types';
import useComponentDidMount from 'Hooks/useComponentDidMount';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import fr from 'facet-rest';
import moment from 'moment';
import { useRefreshModalItemDeleted } from 'Hooks/useRefreshEvent';
import { addQueryWithReplace, QueryVal } from 'Helpers/Query';

const ManagedAccountPageContextContext = createContext(undefined);

export const CUSTOM_DATE_PERIOD = 'custom';
export const MANAGED_ACCOUNT_PAGE_TAB_QUERY_PARAM = 'managedAccountTab';

// Actions
// Init
const INITIALIZE_STARTED = 'initialize-started';
const INITIALIZE_SUCCEEDED = 'initialize-succeeded';
const INITIALIZE_FAILED = 'initialize-failed';

// Tab
const SET_TAB = 'set-tab';

// Beneficiaries
const BENEFICIARIES_LOADING_STARTED = 'beneficiaries-started';
const BENEFICIARIES_LOADING_SUCCEEDED = 'beneficiaries-succeeded';
const BENEFICIARIES_LOADING_FAILED = 'beneficiaries-refresh-failed';

// Managed Account Activity
const MANAGED_ACCOUNT_ACTIVITY_LOADING_STARTED = 'managed-account-activity-loading-started';
const MANAGED_ACCOUNT_ACTIVITY_LOADING_SUCCEEDED = 'managed-account-activity-loading-succeeded';
const MANAGED_ACCOUNT_ACTIVITY_LOADING_FAILED = 'managed-account-activity-loading-failed';
const SET_TRANSACTION_DATE_PERIOD = 'set-transaction-date';
const SET_TRANSACTION_START_DATE = 'set-transaction-start-date';
const SET_TRANSACTION_END_DATE = 'set-transaction-end-date';
const SET_TRANSACTION_TYPE = 'set-transaction-type';
const INCREASE_TRANSACTION_LIMIT = 'increase-transaction-limit';

// Account Performance
const TOGGLE_SHOW_BENCHMARK = 'toggle_show_benchmark';

// Helper to add to params for get call
const addToParams = (params, paramField, currentFieldState, paramValue) => {
  if (currentFieldState !== undefined && currentFieldState !== null && currentFieldState !== '') {
    const tmpParams = params;
    tmpParams[paramField] = paramValue;
    return tmpParams;
  }

  return params;
};

// Reducer
const reducer = (state, action) => {
  switch (action.type) {
    case INITIALIZE_STARTED: {
      return {
        ...state,
        isInitializing: true,
        initializingError: null,
      };
    }
    case INITIALIZE_SUCCEEDED: {
      return {
        ...state,
        isInitializing: false,
        initializingError: null,
      };
    }
    case INITIALIZE_FAILED: {
      return {
        ...state,
        isInitializing: false,
        initializingError: action.initializationError,
      };
    }
    case SET_TAB: {
      addQueryWithReplace({ [MANAGED_ACCOUNT_PAGE_TAB_QUERY_PARAM]: action.currentTab });
      return {
        ...state,
        currentTab: action.currentTab,
        currentTabResource: action.currentTabResource,
      };
    }
    case SET_TRANSACTION_DATE_PERIOD: {
      return {
        ...state,
        transactionDatePeriod: action.transactionDatePeriod,
      };
    }
    case SET_TRANSACTION_START_DATE: {
      return {
        ...state,
        transactionStartDate: action.transactionStartDate,
      };
    }
    case SET_TRANSACTION_END_DATE: {
      return {
        ...state,
        transactionEndDate: action.transactionEndDate,
      };
    }
    case SET_TRANSACTION_TYPE: {
      return {
        ...state,
        transactionType: action.transactionType,
      };
    }
    case INCREASE_TRANSACTION_LIMIT: {
      return {
        ...state,
        transactionLimit: state.transactionLimit + 25,
      };
    }
    case BENEFICIARIES_LOADING_STARTED: {
      return {
        ...state,
        beneficiaryLoading: true,
        beneficiaryLoadingError: null,
      };
    }
    case BENEFICIARIES_LOADING_SUCCEEDED: {
      let beneficiaryResource = null;
      if (action.beneficiaryCollection.resources().list().length > 0) {
        beneficiaryResource = action.beneficiaryCollection.resources().head();
      }
      return {
        ...state,
        beneficiaryCollection: action.beneficiaryCollection,
        beneficiaryResource,
        beneficiaryLoading: false,
        beneficiaryLoadingError: null,
      };
    }
    case BENEFICIARIES_LOADING_FAILED: {
      return {
        ...state,
        beneficiaryLoading: false,
        beneficiaryLoadingError: action.beneficiaryLoadingError,
      };
    }
    case MANAGED_ACCOUNT_ACTIVITY_LOADING_STARTED: {
      return {
        ...state,
        managedAccountActivityLoading: true,
        managedAccountActivityLoadingError: null,
      };
    }
    case MANAGED_ACCOUNT_ACTIVITY_LOADING_SUCCEEDED: {
      return {
        ...state,
        managedAccountActivityCollection: action.managedAccountActivityCollection,
        managedAccountActivityLoading: false,
        managedAccountActivityLoadingError: null,
      };
    }
    case MANAGED_ACCOUNT_ACTIVITY_LOADING_FAILED: {
      return {
        ...state,
        managedAccountActivityLoading: false,
        managedAccountActivityLoadingError: action.managedAccountActivityLoadingError,
      };
    }
    case TOGGLE_SHOW_BENCHMARK: {
      return {
        ...state,
        showBenchmark: !state.showBenchmark,
      };
    }
    default: {
      throw new Error(`Unhandled ManagedAccountPageContextContext action type: ${action.type}`);
    }
  }
};

const ManagedAccountPageContextProvider = ({
  children,
}) => {
  const {
    pageContentCollection,
    pageContentResource,
    isPageContentLoading,
    pageContentLoadingError,
    refreshPageContentCollection,
  } = usePageContentContext();

  const initialState = {
    // Init fields
    isInitializing: true,
    initializingError: null,

    // Beneficiary fields
    beneficiaryCollection: null,
    beneficiaryLoading: true,
    beneficiaryLoadingError: null,

    // Managed Account Activity
    managedAccountActivityCollection: null,
    managedAccountActivityLoading: true,
    managedAccountActivityLoadingError: null,

    transactionDatePeriod: '',
    transactionStartDate: undefined,
    transactionEndDate: undefined,
    transactionType: '',
    transactionLimit: 25,

    // Tab fields
    currentTabResource: null,

    // Account Performance
    showBenchmark: false,
  };
  const [state, dispatch] = useReducer(reducer, initialState);


  const refreshManagedAccountPageCollection = async () => {
    await refreshPageContentCollection(pageContentCollection);
  };

  const setTab = useCallback(async (tabName) => {
    const [tabResource] = pageContentResource.attr('tabs').resources().list().filter(tab => tab.attr('name') === tabName);

    if (!tabResource) {
      return null;
    }
    dispatch({ type: SET_TAB, currentTab: tabName, currentTabResource: tabResource });
    return null;
  }, [pageContentResource]);

  const setTransactionType = useCallback(async (transactionType) => {
    dispatch({ type: SET_TRANSACTION_TYPE, transactionType });

    return null;
  }, [state.transactionType]);

  const setTransactionDatePeriod = useCallback(async (transactionDatePeriod) => {
    dispatch({ type: SET_TRANSACTION_DATE_PERIOD, transactionDatePeriod });

    return null;
  }, [state.transactionDatePeriod]);

  const setTransactionStartDate = useCallback(async (transactionStartDate) => {
    dispatch({ type: SET_TRANSACTION_START_DATE, transactionStartDate });

    return null;
  }, [state.transactionStartDate]);

  const setTransactionEndDate = useCallback(async (transactionEndDate) => {
    dispatch({ type: SET_TRANSACTION_END_DATE, transactionEndDate });

    return null;
  }, [state.transactionEndDate]);

  const increaseTransactionLimit = useCallback(async () => {
    dispatch({ type: INCREASE_TRANSACTION_LIMIT });
    return null;
  }, []);

  const toggleShowBenchmark = useCallback(async () => {
    dispatch({ type: TOGGLE_SHOW_BENCHMARK });
  }, []);

  const fetchBeneficiaries = useCallback(
    async () => {
      dispatch({ type: BENEFICIARIES_LOADING_STARTED });
      try {
        const beneficiaryCollection = await pageContentResource.relationship(fr.relationships.beneficiaries.name).link('related').get();

        dispatch({ type: BENEFICIARIES_LOADING_SUCCEEDED, beneficiaryCollection });
      } catch (e) {
        dispatch({ type: BENEFICIARIES_LOADING_FAILED, beneficiaryLoadingError: e });
      }
    },
    [pageContentResource],
  );

  const findTab = (tabName) => {
    return pageContentResource?.attr('tabs').resources().findItem(r => r.type() === tabName);
  };

  useEffect(() => {
    fetchBeneficiaries();
  }, [pageContentResource]);


  const fetchManagedAccountActivity = useCallback(
    async (managedAccountTransactionLink) => {
      dispatch({ type: MANAGED_ACCOUNT_ACTIVITY_LOADING_STARTED });
      try {
        let params = {};


        params = addToParams(params, 'transactionType', state.transactionType, state.transactionType);
        params = addToParams(params, 'datePeriod', state.transactionDatePeriod, state.transactionDatePeriod);
        params = addToParams(params, 'limit', state.transactionLimit, state.transactionLimit);
        if (state.transactionDatePeriod === CUSTOM_DATE_PERIOD) {
          params = addToParams(params, 'startDate', state.transactionStartDate, `gte|${moment(state.transactionStartDate).format('YYYY-MM-DD')}`);
          params = addToParams(params, 'endDate', state.transactionEndDate, `lte|${moment(state.transactionEndDate).format('YYYY-MM-DD')}`);
        }

        const managedAccountActivityCollection = await managedAccountTransactionLink?.Get({ params });

        dispatch({ type: MANAGED_ACCOUNT_ACTIVITY_LOADING_SUCCEEDED, managedAccountActivityCollection });
      } catch (e) {
        dispatch({ type: MANAGED_ACCOUNT_ACTIVITY_LOADING_FAILED, managedAccountActivityLoadingError: e });
      }
    },
    [state.transactionType, state.transactionDatePeriod, state.transactionStartDate, state.transactionEndDate, state.transactionLimit],
  );

  useEffect(() => {
    const cb = async () => {
      const transactionsTab = findTab('TransactionsTab');
      if (transactionsTab) {
        await fetchManagedAccountActivity(transactionsTab.relationship(fr.relationships.managedAccountActivity.name).link('related'));
      }
    };
    cb();
  }, [pageContentResource, state.transactionType, state.transactionDatePeriod, state.transactionStartDate, state.transactionEndDate, state.transactionLimit]);

  useRefreshModalItemDeleted(async () => {
    const transactionsTab = findTab('TransactionsTab');
    if (transactionsTab) {
      await fetchManagedAccountActivity(transactionsTab.relationship(fr.relationships.managedAccountActivity.name).link('related'));
    }
  }, []);

  const memoizedValue = useMemo(() => {
    return {
      // Data
      isInitializing: state.isInitializing,
      initializingError: state.initializingError,
      managedAccountPageCollection: pageContentCollection,
      managedAccountPageResource: pageContentResource,
      isManagedAccountPageLoading: isPageContentLoading,
      isManagedAccountPageLoadingError: pageContentLoadingError,
      beneficiaryCollection: state.beneficiaryCollection,
      beneficiaryLoading: state.beneficiaryLoading,
      beneficiaryLoadingError: state.beneficiaryLoadingError,
      currentTab: state.currentTab,
      currentTabResource: state.currentTabResource,
      tabCollection: pageContentResource.attr('tabs'),
      managedAccountActivityCollection: state.managedAccountActivityCollection,
      managedAccountActivityLoading: state.managedAccountActivityLoading,
      managedAccountActivityLoadingError: state.managedAccountActivityLoadingError,
      transactionType: state.transactionType,
      transactionDate: state.transactionDate,
      transactionLimit: state.transactionLimit,
      transactionDatePeriod: state.transactionDatePeriod,
      transactionStartDate: state.transactionStartDate,
      transactionEndDate: state.transactionEndDate,
      showBenchmark: state.showBenchmark,

      // Functions
      refreshManagedAccountPageCollection,
      setTab,
      setTransactionType,
      increaseTransactionLimit,
      setTransactionDatePeriod,
      setTransactionStartDate,
      setTransactionEndDate,
      findTab,
      refreshBeneficiaries: fetchBeneficiaries,
      refreshManagedAccountActivity: fetchManagedAccountActivity,
      toggleShowBenchmark,
    };
  }, [
    state,
    refreshManagedAccountPageCollection,
  ]);

  const loadManagedAccountPageCollection = async () => {
    try {
      dispatch({ type: INITIALIZE_STARTED });

      dispatch({
        type: INITIALIZE_SUCCEEDED,
        managedAccountPageCollection: pageContentCollection,
      });
    } catch (err) {
      dispatch({ type: INITIALIZE_FAILED, initializationError: err });
    }
  };

  useComponentDidMount(loadManagedAccountPageCollection);

  useEffect(() => {
    const cb = async () => {
      try {
        const firstPageContentResource = pageContentCollection.resources().head();
        const selfLink = firstPageContentResource?.link('self');
        if (selfLink?.param('tab')) {
          await setTab(selfLink.param('tab'));
          return;
        }

        const tab = QueryVal(MANAGED_ACCOUNT_PAGE_TAB_QUERY_PARAM);
        if (tab) {
          await setTab(tab);
          return;
        }

        const firstTabResource = firstPageContentResource?.attr('tabs').resources().head();
        const firstTabName = firstTabResource.attr('name');

        await setTab(firstTabName);
      } catch (err) {
        dispatch({ type: INITIALIZE_FAILED, initializationError: err });
      }
    };
    cb();
  }, [pageContentCollection]);

  return (
    <ManagedAccountPageContextContext.Provider value={memoizedValue}>
      {children}
    </ManagedAccountPageContextContext.Provider>
  );
};

// Use ManagedAccountPageContext State Context
const useManagedAccountPageContext = () => {
  const context = useContext(ManagedAccountPageContextContext);
  if (context === undefined) {
    throw new Error('useManagedAccountPageContextContext must be used within a ManagedAccountPageContextProvider');
  }
  return context;
};

ManagedAccountPageContextProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
};

export {
  ManagedAccountPageContextProvider,
  useManagedAccountPageContext,
};
